import { useAui, useAuiState } from '@assistant-ui/react';
import { Badge, Button, type ButtonProps } from '@mantine/core';
import { IconArrowBigRightLineFilled, IconInfoCircle, IconSparklesFilled } from '@tabler/icons-react';
import { use } from 'react';
import { useShallow } from 'zustand/shallow';

import classes from './ShowInsightsTool.module.css';
import { type ShowInsightsInput, ShowInsightsInputSchema, type ShowInsightsOutput } from './schema.ts';
import { StreamingMarkdown } from '#plugin-chat/components/markdown-text/StreamingMarkdown.tsx';
import { ToolAside } from '#plugin-chat/components/tool-display/ToolAside.tsx';
import { ToolContentBubble } from '#plugin-chat/components/tool-display/ToolContentBubble.tsx';
import { type CitationScanMessage, plottedMarkerPairs } from '#plugin-chat/components/tools/show-dashboard/markers.ts';
import type { ToolProps } from '#plugin-chat/components/tools/Tool.ts';
import { ToolsContext } from '#plugin-chat/contexts/ToolsContext.ts';
import { ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { Tool } from '#plugin-chat/lib/tool/Tool.tsx';
import type { ToolResult } from '#plugin-chat/lib/transport/types.ts';

export function ShowInsightsTool(props: ToolProps) {
  return (
    <Tool<ShowInsightsInput, ShowInsightsOutput>
      color="green"
      description={
        <ToolGuidance
          description="Call this tool to display a short ranked list of key insights based on prior tool results."
          whenToUse={[
            'To provide insights and suggested follow-up questions the user can ask.',
            'To summarize results after a multi-step workflow completes.',
          ]}
        >
          <p>
            When a numeric column carries an <code>insights</code> block and the user asks how that measurement behaved
            over time, narrate it in plain business language (domain terms are fine; statistical jargon is not) — this
            is exactly the kind of derived insight that prose alongside a dashboard is for. Lead with a one-line plain
            summary of the overall trend, then give the notable phases and events as short, time-ordered bullets, citing
            the figures the insights provide (when a change happened, how large, how far from normal). Quote the
            concrete numbers: the value with its unit and the deviation as a percentage of normal — "dipped ~45% below
            normal to 4.2" — never in statistical units. When an anomaly is flagged as ongoing at the window end, always
            call it out: the signal was still away from normal when the data ended, and that is usually the finding the
            user cares about most. Where it adds value, follow the facts with a brief, clearly-separated interpretation:
            what the finding likely means, its possible consequences, and a suggested next step, drawing on domain
            knowledge.
          </p>
          <p>
            Order insights by descending deviation — the finding furthest from normal first — then most recent first.
            Every insight must carry its numbers: the magnitude with its unit (rate, volume, count) and the relative
            size of the change (percent change, or percent deviation from normal/expected). Emphasize the key figures
            with Markdown in every insight — a bullet without emphasis is malformed. Write dates in plain language
            ("mid-May", "around end of March", "June 11") — never raw ISO 8601 or timestamp strings. Keep each insight
            to the headline fact and let the follow-up questions carry the detail — the user asks them to dive deeper.
          </p>
          <p>
            For example: "Production rate *increased ~12%* from 26 to 29 MSTB/d around *end of March*", or "Gas rate
            *spiked +1 MSCF/d mid-May*, a *10% increase*, correlating with *lower water cut* in 12 wells".
          </p>
          <p>
            When the preceding dashboard plotted x-axis chart markers, set each insight's <code>marker</code> field to
            the byte-exact label of the marker whose dated event or period that insight describes. Never quote or
            restate the cited label inside the insight text — the <code>marker</code> field is its only place. When no
            plotted marker corresponds, omit <code>marker</code> entirely — never a placeholder like "-" — and never
            cite y-axis markers.
          </p>
          <ul>
            <li>
              <strong>Use concise, plain English statements. Target 10-20 words per insight.</strong>
            </li>
            <li>
              Quote the value with its unit and the deviation % from normal ("dipped ~45% below normal to 4.2 MSTB/d"),
              never statistical units.
            </li>
            <li>
              Keep any interpretation (meaning, consequences, suggested actions) clearly separate from the measured
              facts.
            </li>
            <li>Do not assume that one signal caused another — the insights establish correlation at most.</li>
            <li>Do not invent events, dates, or relationships the insight fields do not contain.</li>
            <li>Avoid statistical jargon (sigma, p-values, method names).</li>
          </ul>
        </ToolGuidance>
      }
      execute={() =>
        ({
          success: true,
          flowSignal: {
            continuation: 'stop',
          },
        }) satisfies ToolResult
      }
      icon={IconInfoCircle}
      message={{
        // Keep the call line minimal — the rendered plan in `execute`'s
        // result message already carries every field the LLM needs, so
        // restating the plan body here would duplicate it inside the
        // transcript's tool-call record.
        call: () => null,
        result: () => null,
      }}
      parameters={ShowInsightsInputSchema}
      render={{
        running: ({ args }) => <InsightsContent args={args} />,
        complete: ({ args }) => <InsightsContent args={args} />,
      }}
      {...props}
    />
  );
}

/**
 * Shared running/complete body: the streamed insight list in the content
 * bubble plus follow-up suggestion buttons portalled outside the bezel.
 */
function InsightsContent({ args: { insights, followUpQuestions } }: { args: Partial<ShowInsightsInput> }) {
  return (
    <>
      <ToolContentBubble>
        <div className={classes.insights}>
          <CitedInsights insights={insights} />
        </div>
      </ToolContentBubble>
      <ToolAside>
        <div className={classes.followUpQuestions}>
          {followUpQuestions?.map((question, index) => (
            <SuggestionButton key={question} prompt={question} style={{ '--index': index }} />
          ))}
        </div>
      </ToolAside>
    </>
  );
}

/**
 * The insight list with chart-marker chips. A chip renders only when the
 * citation matches a marker the preceding dashboard actually plotted (read
 * off its tool result in thread state) and is tinted with that marker's
 * resolved chart color — a fabricated or placeholder citation gets no chip.
 */
function CitedInsights({ insights }: { insights: ShowInsightsInput['insights'] | undefined }) {
  const tools = use(ToolsContext);
  // Flat [label, color, …] pairs — an object-shaped selector result would
  // churn identity on every streamed token (see plottedMarkerPairs).
  const pairs = useAuiState(
    useShallow((state) =>
      plottedMarkerPairs(
        state.thread.messages as readonly CitationScanMessage[],
        state.message.index,
        tools.ShowDashboard,
      ),
    ),
  );
  const colorByLabel = new Map(
    Array.from({ length: pairs.length / 2 }, (_, i) => [pairs[2 * i] as string, pairs[2 * i + 1] as string] as const),
  );
  return (
    <ul>
      {insights
        ?.filter((insight) => typeof insight?.text === 'string')
        .map((insight) => {
          const markerColor = insight.marker === undefined ? undefined : colorByLabel.get(insight.marker);
          return (
            <li className={classes.insightItemContainer} key={insight.text}>
              <IconArrowBigRightLineFilled className={classes.icon} color="var(--mantine-color-blue-4)" size={18} />
              <div className={classes.insightItem}>
                <div className={classes.insight}>
                  {insight.text !== undefined && <StreamingMarkdown text={insight.text} />}
                  {insight.marker !== undefined && markerColor !== undefined && (
                    <Badge className={classes.marker} color={markerColor} radius="xs" size="sm" variant="light">
                      {insight.marker}
                    </Badge>
                  )}
                </div>
              </div>
            </li>
          );
        })}
    </ul>
  );
}
function SuggestionButton({ prompt, ...props }: ButtonProps & { prompt: string }) {
  const aui = useAui();

  const trigger = () => {
    aui.thread().append({
      content: [{ type: 'text', text: prompt }],
      runConfig: aui.composer().getState().runConfig,
    });

    aui.composer().setText('');
  };

  return (
    <Button
      classNames={{ label: classes.optionButtonLabel, root: classes.optionButton }}
      color="dark.6"
      leftSection={<IconSparklesFilled color="var(--mantine-color-dark-2)" size={16} />}
      onClick={trigger}
      radius="sm"
      variant="light"
      {...props}
    >
      <span className={classes.optionContent}>
        <span className={classes.optionLabel}>{prompt}</span>
        {/* {option.description && <span className={classes.optionDescription}>{option.description}</span>} */}
      </span>
    </Button>
  );
}
